import { useQuery } from '@tanstack/react-query';
import { useStore } from 'zustand';

import { getCurrentUserDetails } from '@dynatrace-sdk/app-environment';
import {
  isForbidden,
  isNotFound,
  isUnauthorized,
  stateClient,
} from '@dynatrace-sdk/client-state';

import { type AppStateContextValue } from './AppStateContext.js';

const currentUser = getCurrentUserDetails();

/**
 * @internal
 */
export interface _SetOptions {
  validUntilTime?: string | null;
}

/**
 * @internal
 */
export type _UseAppStateResult<T> = {
  value: T | undefined;
  isLoading: boolean;
  error: Error | null;
  setValue: (
    value: T | ((prev: T | undefined) => T),
    options?: _SetOptions,
  ) => Promise<void>;
  remove: () => Promise<void>;
};

type MutateFns = {
  set: (
    key: string,
    value: string,
    validUntilTime: string | null | undefined,
  ) => Promise<void>;
  remove: (key: string) => Promise<void>;
};

export const userScopedMutateFns: MutateFns = {
  set(key: string, value: string, validUntilTime: string | null | undefined) {
    return stateClient.setUserAppState({
      key,
      body: {
        value,
        validUntilTime: validUntilTime ?? null,
        modificationInfo: {
          lastModifiedBy: currentUser.id,
          lastModifiedTime: new Date(),
        },
      },
    });
  },
  remove(key: string) {
    return stateClient.deleteUserAppState({ key });
  },
};

export const appScopedMutateFns: MutateFns = {
  set(key: string, value: string, validUntilTime: string | null | undefined) {
    return stateClient.setAppState({
      key,
      body: {
        value,
        validUntilTime: validUntilTime ?? null,
        modificationInfo: {
          lastModifiedBy: currentUser.id,
          lastModifiedTime: new Date(),
        },
      },
    });
  },
  remove(key: string) {
    return stateClient.deleteAppState({ key });
  },
};

/**
 * @internal
 */
export function useAppStateInternal<T = unknown>(
  key: string,
  mutateFns: MutateFns,
  { store: activeStore, scope, queryClient }: AppStateContextValue,
  enabled: boolean = true,
): _UseAppStateResult<T> {
  const rawValue = useStore(activeStore, (s) => s.data[key]);
  const fetchedKeys = useStore(activeStore, (s) => s.fetchedKeys);
  const keyErrors = useStore(activeStore, (s) => s.keyErrors);
  const storeIsLoading = useStore(activeStore, (s) => s.isLoading);
  const bulkError = useStore(activeStore, (s) => s.error);

  const isFetched = fetchedKeys.has(key);
  const keyError = keyErrors[key] ?? null;

  // Fire an individual GET only when the key's state is not yet known: not
  // fetched, not errored, no ongoing or failed bulk fetch that covers it,
  // and the caller has not disabled this hook via the `enabled` flag.
  const needsIndividualFetch =
    enabled &&
    !isFetched &&
    keyError === null &&
    !storeIsLoading &&
    bulkError === null;

  const { isLoading: isKeyFetchLoading } = useQuery(
    {
      queryKey: ['app-state', scope, key],
      // Sync directly into the Zustand store inside the async queryFn so no
      // effects are needed. onSuccess/onError callbacks were removed in
      // TanStack Query v5; this is the recommended alternative.
      queryFn: async ({ signal }) => {
        try {
          const result =
            scope === 'user'
              ? await stateClient.getUserAppState({ key, abortSignal: signal })
              : await stateClient.getAppState({ key, abortSignal: signal });
          // Mirror the value into Zustand so rawValue is the single source of
          // truth. Skip the write when a concurrent remove() is in progress —
          // the in-flight response must not resurrect a key being deleted.
          activeStore.setState((s) => {
            if (s.deletingKeys.has(key)) {
              return {};
            }
            const update: {
              fetchedKeys: ReadonlySet<string>;
              data?: Record<string, string>;
            } = { fetchedKeys: new Set([...s.fetchedKeys, key]) };
            if (result.value !== undefined) {
              update.data = { ...s.data, [key]: result.value };
            }
            return update;
          });
          return result;
        } catch (error: unknown) {
          if (!(error instanceof Error)) {
            throw error;
          }

          if (error.name === 'AbortError') {
            throw error;
          }

          // 404 — key is genuinely absent. Resolve (not reject) so React Query
          // stores success state; keyFetchData?.value will be undefined below.
          if (isNotFound(error)) {
            activeStore.setState((s) => ({
              fetchedKeys: new Set([...s.fetchedKeys, key]),
            }));
            return null;
          }
          // Real error — store for consumer access and no-retry guarantee.
          activeStore.setState((s) => ({
            keyErrors: { ...s.keyErrors, [key]: error },
          }));
          // Permission errors (401/403) are expected; consumers handle them via
          // the returned error field. Only unexpected failures reach onError.
          if (!isForbidden(error) && !isUnauthorized(error)) {
            activeStore.getState().onError?.(error);
          }
          throw error;
        }
      },
      enabled: needsIndividualFetch,
      throwOnError: false,
    },
    queryClient,
  );

  const isLoading =
    storeIsLoading || (needsIndividualFetch && isKeyFetchLoading);

  // rawValue is the single source of truth: populated by both bulk load
  // (AppStateLoader) and individual fetch (queryFn above). React Query is only
  // used to drive loading state, not to surface the final value.
  const value =
    rawValue !== undefined ? (JSON.parse(rawValue) as T) : undefined;

  // keyErrors is populated inside queryFn before the promise rejects, so it is
  // always the source of truth for individual fetch errors.
  const error = keyError ?? bulkError;

  async function set(
    newValue: T | ((prev: T) => T),
    options?: _SetOptions,
  ): Promise<void> {
    // The bulk query cache is the source of truth for values loaded via
    // AppStateLoader — it is populated synchronously when the bulk fetch
    // promise resolves, while the store is only synced via a re-render cycle
    // that may lag behind under batched updates.
    const bulkCached = queryClient
      .getQueryData<Array<{ key: string; value?: string }>>([
        'app-state',
        scope,
      ])
      ?.find((item) => item.key === key)?.value;
    // Fall back to per-key React Query cache for individually-fetched values.
    const perKeyCached = queryClient.getQueryData<{ value: string }>([
      'app-state',
      scope,
      key,
    ])?.value;
    const previous =
      bulkCached ?? activeStore.getState().data[key] ?? perKeyCached;

    let updatedValue: T;
    if (typeof newValue === 'function') {
      const currentValue =
        previous !== undefined ? (JSON.parse(previous) as T) : undefined;
      updatedValue = (newValue as (prev: T | undefined) => T)(currentValue);
    } else {
      updatedValue = newValue as T;
    }

    try {
      const serialized = JSON.stringify(updatedValue);

      // Optimistic update — also mark the key as fetched so the individual
      // GET does not fire after the write lands in the Zustand store.
      activeStore.setState((s) => ({
        data: { ...s.data, [key]: serialized },
        fetchedKeys: new Set([...s.fetchedKeys, key]),
      }));

      await mutateFns.set(key, serialized, options?.validUntilTime);
      // Update bulk query cache so no follow-up GET is needed.
      queryClient.setQueryData(
        ['app-state', scope],
        (old: Array<{ key: string; value?: string }> | undefined) => {
          if (!old) {
            return old;
          }
          const idx = old.findIndex((item) => item.key === key);
          if (idx === -1) {
            return [...old, { key, value: serialized }];
          }
          return old.map((item, i) =>
            i === idx ? { ...item, value: serialized } : item,
          );
        },
      );
    } catch (err) {
      // Rollback value — keep key in fetchedKeys (its existence is known).
      activeStore.setState((s) => {
        const next = { ...s.data };
        if (previous === undefined) {
          delete next[key];
        } else {
          next[key] = previous;
        }
        return { data: next };
      });
      activeStore.getState().onError?.(err as Error);
    }
  }

  async function remove(): Promise<void> {
    const previous = activeStore.getState().data[key];

    // Mark the key as being deleted before the async call. Any in-flight
    // individual GET that resolves while the DELETE is pending will see this
    // tombstone and skip its Zustand write, preventing a resurrection.
    activeStore.setState((s) => {
      const next = { ...s.data };
      delete next[key];
      return {
        data: next,
        deletingKeys: new Set([...s.deletingKeys, key]),
      };
    });

    try {
      await mutateFns.remove(key);
      // Tombstone is intentionally NOT cleared on success. An in-flight
      // individual GET may still resolve after the DELETE — keeping
      // deletingKeys populated ensures its write is silently discarded.
      // Evict the per-key cache so a later functional setValue(prev => ...)
      // cannot read the deleted value from perKeyCached.
      queryClient.removeQueries({ queryKey: ['app-state', scope, key] });
      // Update bulk query cache.
      queryClient.setQueryData(
        ['app-state', scope],
        (old: Array<{ key: string; value?: string }> | undefined) => {
          if (!old) {
            return old;
          }
          return old.filter((item) => item.key !== key);
        },
      );
    } catch (err) {
      // Rollback: clear tombstone and restore the previous value so
      // future individual GETs can write back correctly.
      activeStore.setState((s) => {
        const deletingKeys = new Set(s.deletingKeys);
        deletingKeys.delete(key);
        const data =
          previous !== undefined ? { ...s.data, [key]: previous } : s.data;
        return { deletingKeys, data };
      });
      // If the key wasn't in Zustand when remove() was called, a concurrent
      // in-flight GET may have resolved and been blocked by the tombstone. Its
      // result is cached by React Query with staleTime: Infinity, so the query
      // won't re-fire on its own. Evict the cache entry so the query re-runs
      // and populates Zustand with the current server value.
      if (previous === undefined) {
        queryClient.removeQueries({ queryKey: ['app-state', scope, key] });
      }
      activeStore.getState().onError?.(err as Error);
    }
  }

  return { value, isLoading, error, setValue: set, remove };
}
